import React, { ReactNode, useState, useEffect, useCallback } from 'react';
import { Box, CssBaseline } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import toast from 'react-hot-toast';
import { useTaskNotifications, VideoPatientJoined } from '../../hooks/useTaskNotifications';
import { useVideoCall } from '../../contexts/VideoCallContext';
import internalTaskService from '../../services/internalTaskService';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const configSyncAvailable = !!user?.user_privilege?.config_sync;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { currentTenant } = useTenant();

  // Internal-tasks "needs action" badge count. Fetched once on auth, then kept
  // live via SSE (useTaskNotifications refetches on tasks_count_changed events).
  const [taskCount, setTaskCount] = useState(0);
  useEffect(() => {
    if (!user) {
      setTaskCount(0);
      return;
    }
    internalTaskService.getNeedsActionCount().then(setTaskCount).catch(() => {});
  }, [user]);
  // A patient arriving in a video room is time-critical - the therapist may be on
  // another screen entirely - so it interrupts with a toast that can start the call
  // directly rather than only updating a badge somewhere.
  const { startCall } = useVideoCall();
  const handlePatientJoined = useCallback((event: VideoPatientJoined) => {
    const meetingId = Number(event.external_id);
    const who = event.patient_name || 'Your patient';
    toast.success(
      (tst) => (
        <span className="flex items-center gap-3">
          <span>{who} joined the video room</span>
          {Number.isFinite(meetingId) && (
            <button
              onClick={() => {
                toast.dismiss(tst.id);
                startCall(meetingId);
              }}
              className="px-3 py-1 rounded-lg bg-[#3A7380] text-white text-sm hover:bg-[#2D5A60] transition-colors"
            >
              Join
            </button>
          )}
        </span>
      ),
      // Long, but dismissible: a patient sitting alone in a room is worth
      // interrupting for, and a 4-second toast is easily missed.
      { duration: 30000, icon: '📹' },
    );
  }, [startCall]);

  useTaskNotifications(!!user, setTaskCount, handlePatientJoined);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = () => {
    logout();
  };

  // Get active menu item based on current path
  const getActiveItem = () => {
    const path = location.pathname;
    // Check for patient detail page (e.g., /patients/123)
    if (path.match(/^\/patients\/\d+$/)) return 'patient-page';
    if (path.includes('patients')) return 'patients';
    if (path.includes('meetings')) return 'meetings';
    if (path.includes('calendar')) return 'calendar';
    if (path.includes('questionnaires') && !path.includes('questionnaire-types')) return 'questionnaires';
    if (path.includes('sms-templates')) return 'sms-templates';
    if (path.includes('email-templates')) return 'email-templates';
    if (path.includes('user-privileges')) return 'user-privileges';
    if (path.includes('users')) return 'users';
    if (path.includes('tenants')) return 'tenants';
    if (path.includes('referrals')) return 'referrals';
    if (path.includes('clinics')) return 'clinics';
    if (path.includes('meeting-types')) return 'meeting-types';
    if (path.includes('summary-types')) return 'summary-types';
    if (path.includes('file-types')) return 'file-types';
    if (path.includes('patient-status-configs')) return 'patient-status-configs';
    if (path.includes('patient-types')) return 'patient-types';
    if (path.startsWith('/services')) return 'services';
    if (path.includes('patient-field-configs')) return 'patient-field-configs';
    if (path.includes('patient-notifications')) return 'patient-notifications';
    if (path.includes('medications')) return 'medications';
    if (path.includes('questionnaire-types')) return 'questionnaire-types';
    if (path.includes('prompts')) return 'prompts';
    if (path.includes('unmatched-communications')) return 'unmatched-communications';
    if (path.includes('activity-logs')) return 'activity-logs';
    if (path.includes('wellness-configs')) return 'wellness-configs';
    if (path.includes('wellness-items')) return 'wellness-items';
    if (path.includes('wellness-tips')) return 'wellness-tips';
    if (path.includes('diagnosis-types')) return 'diagnosis-types';
    return 'calendar';
  };

  const sidebarWidth = sidebarCollapsed ? 60 : 200;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <Header
        userName={user?.full_name || `${user?.first_name} ${user?.last_name}` || 'User'}
        tenantName={currentTenant?.name}
        onLogout={handleLogout}
        onMenuClick={handleSidebarToggle}
        showMenuButton={false}
      />
      <Box sx={{ display: 'flex', flexGrow: 1, pt: '60px', position: 'relative' }}>
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          activeItem={getActiveItem()}
          onNavigate={(path: string) => navigate(path)}
          user={user}
          currentPath={location.pathname}
          configSyncAvailable={configSyncAvailable}
          taskCount={taskCount}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            ml: `${sidebarWidth}px`,
            transition: 'margin-left 0.3s ease',
            width: '100%',
            boxSizing: 'border-box',
            maxWidth: `calc(100% - ${sidebarWidth}px)`,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
